/**
 * Fight cost resolution for the Legendary Arena game engine (WP-214).
 *
 * resolveFightCost is the single authoritative source for villain fight cost.
 * Static villains return their fightCost directly. Dynamic villains (vAttack
 * "*" or "N+") return fightCostBase plus the sum of captured hero recruit
 * costs. Scheme bonuses stack on top: the Portals Dark-Portal space bonus
 * (WP-539) and the Midtown Bank Robbery family's +1 per attached Bystander
 * (WP-748). A Blood Frenzy villain adds the fighting player's distinct Victory
 * Point value count (WP-760). An Abomination villain or mastermind adds the printed Attack of
 * HQ Heroes (WP-815). A variable-Attack villain adds its printed passive, counted at
 * fight time (WP-841). The UI must never recompute dynamic values — it consumes the
 * engine-resolved projection from UIState.
 *
 * No boardgame.io import. No ctx dependency. No randomness. No .reduce().
 * Always returns a deterministic integer >= 0.
 */
// why: WP-513 / D-24325 — value import for the Killbots per-scheme twist counter key.
// why: WP-539 / D-24348 — DARK_PORTAL_COUNT drives the Portals Dark-Portal buffs.
import { KILLBOT_TWISTS_NEXT_TO_SCHEME, DARK_PORTAL_COUNT } from '../types.js';
// why: WP-823 / D-24756 — the Traitor Battalion counter and the Officer base Attack
// drive a Brainwash the Military Officer's fight cost.
import { TRAITOR_BATTALION_TWISTS } from '../types.js';
import { BRAINWASHED_OFFICER_BASE_ATTACK } from '../rules/brainwashTheMilitary.logic.js';
// why: WP-728 / D-24549 — the fixed City space count bounds the Dark-Portal
// location scan; CITY_SPACE_NAMES is the single source of the 5-space board.
import { CITY_SPACE_NAMES, citySpaceNameForIndex } from '../board/citySpaceNames.js';
// why: WP-760 / D-24589 — villain Blood Frenzy reuses WP-765's shared distinct-VP
// helper (D-24598), so villain and hero Blood Frenzy can never count differently.
import { countDistinctVictoryPointValues } from './bloodFrenzy.logic.js';
// why: WP-794 / D-24663 — the only read site of TurnEconomy.fightCostReductions is the
// shared summing helper, called from the two resolvers below.
import { getFightCostReduction } from './economy.logic.js';
// why: WP-815 / D-24747 — the Abomination terms read the HQ at fight time; one
// helper per resolver, so each family is added in exactly one authority.
import { villainAbominationBonus, mastermindAbominationBonus } from './abomination.logic.js';
// why: WP-841 / D-24774 — the variable-Attack passives read the City, the Escape Pile
// and the HQ at fight time; the villain resolver is their only reader.
import { villainVariableAttackBonus } from './villainVariableAttack.logic.js';
// why: WP-818 / D-24752 — Baron Zemo's "+9 unless you are a Savior" reads the fighter's
// Savior status through the shared WP-816 helpers, so the cost and every Savior gate count
// the same Bystanders (defeated Killbots included).
import { SAVIOR_BYSTANDER_THRESHOLD, countVictoryPileBystanders } from '../board/bystanders.logic.js';
// why: WP-818 / D-24752 — the Zemo mastermind id has one home (WP-819 / D-24753); it is
// id-keyed like the Master Strike dispatch (D-24300), because Zemo is the only Mastermind
// that prints "+N unless you are a Savior".
import { BARON_ZEMO_MASTERMIND_ID } from './rescueOnFight.logic.js';
// why: D-24802 — the fight side of Size-Changing shares the recruit side's discount step
// (D-24801), so the two can never count played cards differently.
import { applySizeChangingDiscount } from '../hero/sizeChanging.logic.js';
// why: WP-539 / D-24348 — the Portals scheme ext_id, gating the Dark-Portal buffs.
const PORTALS_SCHEME_ID = 'core/portals-to-the-dark-dimension';
// why: WP-818 / D-24752 — Baron Zemo's printed "+9[icon:attack] unless you are a Savior".
/** The Attack Baron Zemo gains against a fighter who is not a Savior. */
export const BARON_ZEMO_NON_SAVIOR_BONUS = 9;
// why: WP-728 / D-24549 — the +N attack a single Dark Portal grants (the
// Mastermind, or Villains in a portal'd city space). A named constant so the
// combat buffs (below) and the UIState projection (darkPortalLocations
// consumers) share ONE value and can never disagree.
export const DARK_PORTAL_ATTACK_BONUS = 1;
// why: WP-748 / D-24572 — the schemes whose Special Rules print "Each Villain gets
// +1 attack for each Bystander it has": core Midtown Bank Robbery, the co2e Bank
// Robbery Hostage Crisis reprint, and msp1 Destroy the Cities of Earth. A card-data
// scan of every set finds exactly these three. Consumed only by
// bystanderVillainAttackBonus.
const VILLAIN_ATTACK_PER_BYSTANDER_SCHEME_IDS = new Set([
    'core/midtown-bank-robbery',
    'co2e/bank-robbery-hostage-crisis',
    'msp1/destroy-the-cities-of-earth',
]);
/**
 * Resolves the fight cost for a villain at the current game state.
 *
 * For static villains (fightCostMode === 'static'), returns fightCost
 * unchanged. For dynamic villains (fightCostMode === 'dynamic'), returns
 * fightCostBase + sum(captured hero recruit costs). Scheme bonuses are then
 * added: the Portals Dark-Portal space bonus (darkPortalVillainBonus) and the
 * Midtown Bank Robbery family's per-Bystander bonus (bystanderVillainAttackBonus).
 * A Blood Frenzy villain then adds the fighting player's distinct Victory Point
 * value count (villainBloodFrenzyBonus), and an Abomination villain the printed
 * Attack of the HQ Hero under its City space (villainAbominationBonus).
 *
 * Tolerates: missing cardStats entry (returns 0), no attached heroes
 * (returns fightCostBase), missing cardStats for a captured hero (treats
 * as 0). Always returns a deterministic integer >= 0.
 *
 * @param G - Game state (read-only).
 * @param villainCardId - The villain zone-instance ext_id.
 * @param fightingPlayerId - The player who would fight (the active player). When
 *   omitted, the Blood Frenzy term is 0.
 * @returns The resolved fight cost as a non-negative integer.
 */
export function resolveFightCost(G, villainCardId, fightingPlayerId) {
    // why: scheme bonuses stack on top of every villain's resolved cost (static,
    // dynamic, or a converted Killbot/Skrull), so they are applied here around the
    // base resolution: the Portals Dark-Portal bonus (WP-539 / D-24348) and the
    // Midtown Bank Robbery family's +1 per Bystander (WP-748 / D-24572). This is the
    // single site, so the fight move, the bot's legal moves and the City fightCost
    // projection can never disagree. Blood Frenzy (WP-760 / D-24589) is a villain
    // keyword, not a scheme bonus, but it lives here for the same reason.
    // why: WP-815 / D-24747 — Abomination / Double Abomination (rules v23 L880-926) adds
    // the printed Attack of the HQ Hero under the Villain's City space, read at fight
    // time because the cost moves with the City and the HQ. Added before the reduction.
    // why: WP-841 / D-24774 — a printed "<Villain> gets +N Attack …" passive (Baron
    // Strucker, Paibok, Super-Skrull, Hydro-Man) is read at fight time from the City,
    // the Escape Pile and the HQ, and added before the reduction and the 0 floor.
    const costBeforeReduction = resolveBaseFightCost(G, villainCardId) +
        darkPortalVillainBonus(G, villainCardId) +
        bystanderVillainAttackBonus(G, villainCardId) +
        villainBloodFrenzyBonus(G, villainCardId, fightingPlayerId) +
        villainAbominationBonus(G, villainCardId) +
        villainVariableAttackBonus(G, villainCardId);
    // why: WP-794 / D-24663 — the Storm / Forge "Any Villain you fight on the <space> this turn
    // gets -N attack" reduction is applied HERE, in the single authority, so the fight move, the
    // bot's legal moves and the City fightCost projection all agree by construction.
    // why: the 0 floor keeps this function's ">= 0" promise; Patrol (getPatrolModifier) is added
    // by the fight move AFTER this floor, unchanged, so Patrol still pays in full.
    return Math.max(0, costBeforeReduction - cityFightCostReduction(G, villainCardId));
}
/**
 * Resolves what the fighting player pays to fight a City villain right now (D-24802).
 *
 * The villain's resolved Attack (resolveFightCost) minus its Size-Changing discount: 2 less
 * for each listed class with a matching card the fighter played this turn, floored at 0
 * ("A Villain's Attack can't go below 0", rules v23 ~L3418), then 2 less for each
 * Microscopic icon matched by its own played card, which may go below 0 (rules v23 p.30:
 * "if you fight a Villain with Microscopic Size-Changing and reduce its value to a negative
 * number, you gain that many [Attack] when you fight it"). Patrol is added by the caller.
 *
 * // why: a separate read from resolveFightCost, like resolveRecruitCost beside the printed
 * hero cost (D-24801): the discount applies only to the fight. Effects that read a villain's
 * Attack ("less than N Attack", Pure Fury) keep reading resolveFightCost.
 *
 * @param G - Game state (read-only).
 * @param villainCardId - The villain zone-instance ext_id.
 * @param fightingPlayerId - The player who would fight. When omitted, no discount applies.
 * @returns The fight price; negative only through Microscopic Size-Changing.
 */
export function resolveFightPrice(G, villainCardId, fightingPlayerId) {
    const villainAttack = resolveFightCost(G, villainCardId, fightingPlayerId);
    return applyFightSizeChanging(G, villainCardId, villainAttack, fightingPlayerId);
}
/**
 * Resolves what the fighting player pays to fight the Mastermind right now (D-24802).
 *
 * resolveMastermindFightCost minus the current face's Size-Changing discount, floored at 0.
 *
 * // why: floored at 0, unlike the villain price. The only Microscopic Mastermind face
 * (amwp Yellowjacket) is the flip side of Darren Cross, which is not in the transform
 * allowlist, so no match can fight it yet; the below-0 gain for a Mastermind lands with
 * that face. Fin Fang Foom's regular Size-Changing floors at 0 either way.
 *
 * @param G - Game state (read-only).
 * @param fightingPlayerId - The player fighting the Mastermind. When omitted, no discount applies.
 * @returns The Mastermind fight price as a non-negative integer.
 */
export function resolveMastermindFightPrice(G, fightingPlayerId) {
    const mastermindAttack = resolveMastermindFightCost(G, fightingPlayerId);
    return Math.max(0, applyFightSizeChanging(G, G.mastermind.baseCardId, mastermindAttack, fightingPlayerId));
}
/**
 * Applies a card's Size-Changing fight discount to its resolved Attack (D-24802).
 *
 * @param G - Game state (read-only).
 * @param cardId - The villain instance or mastermind face id.
 * @param attack - The card's resolved Attack.
 * @param fightingPlayerId - The fighter, whose played cards drive the discount.
 * @returns The discounted value, or `attack` unchanged when the card has no discount.
 */
function applyFightSizeChanging(G, cardId, attack, fightingPlayerId) {
    const discount = G.fightSizeChanging?.[cardId];
    // why: the discount reads the fighter's played cards, so with no fighter there is nothing
    // to count (the Blood Frenzy convention).
    if (discount === undefined || fightingPlayerId === undefined) {
        return attack;
    }
    return applySizeChangingDiscount(G, fightingPlayerId, attack, discount.classes, discount.microscopicIcons);
}
/**
 * The fight-cost reduction for a villain's current City space (WP-794 / D-24663).
 *
 * Sums the turn's reductions whose target is the City space the villain is in
 * right now. A villain not in the City, an index with no space name, or a partial
 * `G` with no `turnEconomy` gets 0.
 *
 * @param G - Game state (read-only).
 * @param villainCardId - The villain zone-instance ext_id.
 * @returns The reduction for the villain's space, or 0.
 */
function cityFightCostReduction(G, villainCardId) {
    // why: the space is read at FIGHT time, never stored at play time, so a Villain that
    // enters or is moved into the space later this turn still gets the reduction. The
    // `?.` / `?? -1` mirror darkPortalVillainBonus's partial-G tolerance.
    const cityIndex = G.city?.indexOf(villainCardId) ?? -1;
    if (cityIndex < 0 || G.turnEconomy === undefined) {
        return 0;
    }
    const spaceName = citySpaceNameForIndex(cityIndex);
    if (spaceName === undefined) {
        return 0;
    }
    return getFightCostReduction(G.turnEconomy, spaceName);
}
/**
 * The Blood Frenzy attack bonus for a villain (WP-760 / D-24589).
 *
 * A villain flagged in G.villainBloodFrenzy gets +1 attack for each different
 * Victory Point value among the cards in the fighting player's Victory Pile.
 * Unflagged villains, and any call without a fighting player, get 0.
 *
 * @param G - Game state (read-only).
 * @param villainCardId - The villain zone-instance ext_id.
 * @param fightingPlayerId - The player who would fight, if known.
 * @returns The distinct VP value count, or 0.
 */
function villainBloodFrenzyBonus(G, villainCardId, fightingPlayerId) {
    // why: Blood Frenzy reads the FIGHTER's Victory Pile, so the term needs a player.
    // The parameter is optional so every pre-WP-760 caller and test stays
    // byte-identical; the three production callers pass the active player — the only
    // player who can fight — so the City projection shows that player's cost to every
    // audience.
    if (fightingPlayerId === undefined) {
        return 0;
    }
    if (G.villainBloodFrenzy?.[villainCardId] !== true) {
        return 0;
    }
    return countDistinctVictoryPointValues(G, fightingPlayerId);
}
/**
 * The Portals Dark-Portal locations for the current game state (WP-728 / D-24549).
 *
 * The single source of the portal→location mapping: the Dark Portal above the
 * Mastermind opens on twist 1 (DARK_PORTAL_COUNT >= 1), and city space index K is
 * portal'd once DARK_PORTAL_COUNT >= 6 - K (twists 2-6 fill the leftmost
 * portal-less space first; leftmost = Bridge = index 4 per WP-489/D-24295). Both
 * the combat buffs (darkPortalVillainBonus / resolveMastermindFightCost) and the
 * UIState projection (uiState.build) read this helper, so combat and the UI can
 * never disagree on where the portals are. A non-Portals scheme (or a missing
 * selection/counter) yields no portals.
 *
 * @param G - Game state (read-only).
 * @returns onMastermind + the ascending list of portal'd city space indices.
 */
export function darkPortalLocations(G) {
    // why: defensive `?.` mirrors this module's partial-G tolerance (integration /
    // unit fixtures may omit selection/counters); a missing field means "not the
    // Portals scheme" → no portals.
    if (G.selection?.schemeId !== PORTALS_SCHEME_ID) {
        return { onMastermind: false, citySpaceIndices: [] };
    }
    const portalCount = G.counters?.[DARK_PORTAL_COUNT] ?? 0;
    const citySpaceIndices = [];
    // why: the `6 - K` fill predicate is the SAME one this helper's callers used
    // inline before; an explicit index loop over the fixed 5 City spaces (no
    // .reduce()), collecting portal'd indices ascending.
    for (let cityIndex = 0; cityIndex < CITY_SPACE_NAMES.length; cityIndex++) {
        if (portalCount >= 6 - cityIndex) {
            citySpaceIndices.push(cityIndex);
        }
    }
    return { onMastermind: portalCount >= 1, citySpaceIndices };
}
/**
 * The Portals Dark-Portal attack bonus for a villain (WP-539 / D-24348).
 *
 * Under the Portals scheme, a Villain in a city space that has a Dark Portal
 * attacks for DARK_PORTAL_ATTACK_BONUS more. A Villain not in the City (index not
 * found) and any non-Portals scheme get 0.
 *
 * @param G - Game state (read-only).
 * @param villainCardId - The villain zone-instance ext_id.
 * @returns DARK_PORTAL_ATTACK_BONUS when the villain's city space has a Dark
 *   Portal, else 0.
 */
function darkPortalVillainBonus(G, villainCardId) {
    // why: the villain's own city index gates the buff; darkPortalLocations is the
    // single source for WHICH spaces are portal'd (it also gates the Portals scheme
    // + partial-G tolerance, so a non-Portals scheme returns no portal'd indices).
    const cityIndex = G.city?.indexOf(villainCardId) ?? -1;
    if (cityIndex < 0) {
        return 0;
    }
    return darkPortalLocations(G).citySpaceIndices.includes(cityIndex)
        ? DARK_PORTAL_ATTACK_BONUS
        : 0;
}
/**
 * The Midtown Bank Robbery family's attack bonus for a villain (WP-748 / D-24572).
 *
 * Under a scheme in VILLAIN_ATTACK_PER_BYSTANDER_SCHEME_IDS, a Villain (henchmen
 * included) gets +1 attack for each Bystander it holds in G.attachedBystanders.
 * Any other scheme gets 0. The Mastermind is not a Villain and never reads this.
 *
 * @param G - Game state (read-only).
 * @param villainCardId - The villain zone-instance ext_id.
 * @returns The number of Bystanders the villain holds under a family scheme, else 0.
 */
function bystanderVillainAttackBonus(G, villainCardId) {
    // why: defensive `?.` mirrors this module's partial-G tolerance — the Portals and
    // Mastermind fixtures build G under Midtown with no selection or no
    // attachedBystanders map, and a missing map means "holds no Bystanders".
    const schemeId = G.selection?.schemeId;
    if (schemeId === undefined || !VILLAIN_ATTACK_PER_BYSTANDER_SCHEME_IDS.has(schemeId)) {
        return 0;
    }
    return G.attachedBystanders?.[villainCardId]?.length ?? 0;
}
/**
 * Resolves the base fight cost for a villain (WP-214), before any scheme-driven
 * bonus. See resolveFightCost for the Portals Dark-Portal wrapper.
 *
 * @param G - Game state (read-only).
 * @param villainCardId - The villain zone-instance ext_id.
 * @returns The resolved base fight cost as a non-negative integer.
 */
function resolveBaseFightCost(G, villainCardId) {
    // why: WP-513 / D-24325 — a converted Killbot's attack is dynamic: it equals the
    // per-scheme "twists next to this Scheme" counter (seeded 3, +1 per Killbots
    // twist). Checked OVERLAY-FIRST, before the cardStats guard, because converted
    // villain-deck Bystanders carry no cardStats row (that guard would return 0).
    // Reads G.counters — a deliberate input-widening; the UI still consumes only the
    // engine-resolved value and never recomputes.
    if (G.convertedVillainOrigins?.[villainCardId] === 'killbot') {
        return G.counters[KILLBOT_TWISTS_NEXT_TO_SCHEME] ?? 0;
    }
    // why: WP-514 / D-24327 — a converted Skrull (a Hero shuffled into the Villain Deck
    // by Secret Invasion) attacks for the Hero's cost + 2. Checked OVERLAY-FIRST, beside
    // the Killbot branch. PROXY: the printed attack is the Hero's VP + 2, but hero VP
    // exists nowhere in the data (generated or upstream — buildCardVictoryPoints emits
    // villains/henchmen/masterminds only), so we approximate with the Hero's cost (present
    // in G.cardStats). Swap seam: change `G.cardStats[...]?.cost` to
    // `G.cardVictoryPoints?.[villainCardId]` if hero VP is ever authored. cost typically
    // runs slightly higher than VP, so Skrulls skew a touch harder than printed.
    if (G.convertedVillainOrigins?.[villainCardId] === 'skrull') {
        return (G.cardStats[villainCardId]?.cost ?? 0) + 2;
    }
    // why: WP-823 / D-24756 — a Brainwash the Military Officer's Attack "is 3 plus the
    // number of Twists stacked next to this Scheme" (the Traitor Battalion). Checked
    // OVERLAY-FIRST, beside the Killbot / Skrull branches, because a converted Officer
    // carries no cardStats row (the guard below would return 0).
    if (G.convertedVillainOrigins?.[villainCardId] === 'shield-officer') {
        return BRAINWASHED_OFFICER_BASE_ATTACK + (G.counters[TRAITOR_BATTALION_TWISTS] ?? 0);
    }
    const villainStats = G.cardStats[villainCardId];
    if (villainStats === undefined) {
        return 0;
    }
    // why: treat missing fightCostMode as 'static' for backward-compat with
    // pre-WP-214 G fixtures in integration tests that use partial cardStats
    if (!villainStats.fightCostMode || villainStats.fightCostMode === 'static') {
        return villainStats.fightCost;
    }
    // Dynamic mode: base + sum of captured hero recruit costs
    const base = villainStats.fightCostBase;
    // why: WP-841 / D-24774 (Operator fork F2) — on a variable-Attack villain the `+`
    // marks the printed passive, not a captured-Hero cost, so the passive term in
    // resolveFightCost is the whole variable part and a captured Hero adds nothing.
    if (G.villainVariableAttack?.[villainCardId] !== undefined) {
        return base;
    }
    // why: reads CardStatEntry.cost (hero recruit cost), NOT fightCost
    // (which is the villain's own fight requirement, always 0 for heroes).
    // G.villainAttachedHeroes[v] is undefined (not []) when no heroes are
    // attached — the ?? [] guard is required to avoid iterating undefined.
    // The top-level G.villainAttachedHeroes guard handles pre-WP-214 G fixtures.
    const capturedHeroes = G.villainAttachedHeroes?.[villainCardId] ?? [];
    let heroSum = 0;
    for (const heroId of capturedHeroes) {
        heroSum += G.cardStats[heroId]?.cost ?? 0;
    }
    return base + heroSum;
}
/**
 * Resolves the Mastermind's fight requirement (WP-539 / D-24348).
 *
 * The base requirement is the mastermind base card's fightCost (WP-018 / D-1805).
 * Under the Portals scheme, the Dark Portal placed above the Mastermind on twist 1
 * adds +1 (once DARK_PORTAL_COUNT >= 1). This centralizes what fightMastermind,
 * uiState.build, and ai.legalMoves previously read inline, so combat, the UI, and
 * the bot never disagree on affordability.
 *
 * Baron Zemo adds BARON_ZEMO_NON_SAVIOR_BONUS for a fighter who is not a Savior
 * (WP-818 / D-24752).
 *
 * @param G - Game state (read-only).
 * @param fightingPlayerId - The player fighting the Mastermind. Optional; when
 *   omitted, a fighter-dependent term assumes the costlier case.
 * @returns The mastermind fight requirement as a non-negative integer.
 */
export function resolveMastermindFightCost(G, fightingPlayerId) {
    const baseFightCost = G.cardStats[G.mastermind.baseCardId]?.fightCost ?? 0;
    // why: the twist-1 Dark Portal above the Mastermind adds DARK_PORTAL_ATTACK_BONUS;
    // darkPortalLocations is the single source (scheme gate + counter read), so this
    // combat read and the UIState projection can never disagree.
    const portalBonus = darkPortalLocations(G).onMastermind ? DARK_PORTAL_ATTACK_BONUS : 0;
    // why: WP-794 / D-24663 — Storm's "[hc:ranged]: The Mastermind gets -2 attack this turn" is
    // applied HERE, in the single authority, so fightMastermind (normal and Final Blow), the bot
    // and the Mastermind fightCost projection agree. A partial G with no turnEconomy gets 0.
    const mastermindReduction = G.turnEconomy === undefined ? 0 : getFightCostReduction(G.turnEconomy, 'mastermind');
    // why: WP-815 / D-24747 — Ultimate / Double Highest Abomination adds the printed Attack
    // of the HQ Heroes, read at fight time (the HQ refills); added before the reduction.
    const abominationBonus = mastermindAbominationBonus(G);
    // why: WP-818 / D-24752 — Baron Zemo's +9 unless the fighter is a Savior; added
    // before the reduction, like the Abomination term.
    const nonSaviorBonus = mastermindNonSaviorBonus(G, fightingPlayerId);
    // why: the 0 floor keeps this function's ">= 0" promise.
    return Math.max(0, baseFightCost + portalBonus + abominationBonus + nonSaviorBonus - mastermindReduction);
}
/**
 * Baron Zemo's "+9 unless you are a Savior" term (WP-818 / D-24752).
 *
 * @param G - Game state (read-only; `selection` may be absent on partial test states).
 * @param fightingPlayerId - The player fighting the Mastermind, when known.
 * @returns BARON_ZEMO_NON_SAVIOR_BONUS when Baron Zemo is the Mastermind and the
 *   fighter is not a Savior, otherwise 0.
 */
function mastermindNonSaviorBonus(G, fightingPlayerId) {
    if (G.selection?.mastermindId !== BARON_ZEMO_MASTERMIND_ID) {
        return 0;
    }
    // why: rules v23 L1844-1853 — "unless you are a Savior" is about the fighter. An omitted
    // or unknown fighter pays the +9 (D-24752), so a caller can never get the cheaper cost
    // by forgetting the argument.
    if (fightingPlayerId === undefined) {
        return BARON_ZEMO_NON_SAVIOR_BONUS;
    }
    const zones = G.playerZones?.[fightingPlayerId];
    if (zones === undefined) {
        return BARON_ZEMO_NON_SAVIOR_BONUS;
    }
    // why: only the fighter's Victory Pile counts. The Bystanders Zemo holds are rescued
    // after the fight, so they never lower the cost that must be known to fight at all.
    if (countVictoryPileBystanders(zones.victory) >= SAVIOR_BYSTANDER_THRESHOLD) {
        return 0;
    }
    return BARON_ZEMO_NON_SAVIOR_BONUS;
}
//# sourceMappingURL=economy.resolve.js.map